import {
  AuthenticationToken,
  Environment,
  hostsConfig,
} from '@openeducation/oe-it-services';
import constate from 'constate';
import React, { useCallback, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { LOGIN_MESSAGES } from './login-messages';
import useLoginReducer from './login.reducer';
import useAuthentication from './use-authentication';
import { useEnvironmentContext } from '../environment/environment.provider';
import type { AuthStorage } from '../interfaces/auth-storage';

export interface LoginContextProps {
  authenticateWithUsernameAndPassword: (
    env: Environment,
    username: string,
    password: string
  ) => Promise<AuthenticationToken>;
  resetUserPassword: (env: Environment, email: string) => Promise<any>;
  validateAuthentication: (authData: AuthenticationToken) => string;
  validateAuthorization?: (id?: number | string) => Promise<boolean>;
  authDataStorage: AuthStorage<string>;
  getIdFromToken: (accessToken: string) => number | string | null;
}

const LoginContext = ({
  authenticateWithUsernameAndPassword,
  validateAuthentication,
  validateAuthorization,
  resetUserPassword,
  authDataStorage,
  getIdFromToken,
}: LoginContextProps) => {
  const { environment } = useEnvironmentContext();
  const [onLoginSuccess, setOnLoginSuccess] = useState<{
    params: any[];
    callback: (params?: any) => void;
  }>(); //We use any here because is not possible to use generics with constate

  const {
    state,
    storageLoadInit,
    storageLoadComplete,
    authenticateSuccess,
    loginSuccess,
    loginFails,
    logoutAsync,
  } = useLoginReducer();

  const authentication = useAuthentication({
    authenticateWithUsernameAndPassword,
  });

  useEffect(() => {
    // undefined means that is the first time that the component is rendered
    if (state.isLoginSuccess === undefined) {
      storageLoadInit();
    }
  }, [storageLoadInit, state.isLoginSuccess]);

  useEffect(() => {
    async function getLocalToken() {
      try {
        const token = await authDataStorage.get();
        storageLoadComplete(token);
      } catch {
        storageLoadComplete();
      }
    }

    getLocalToken();
  }, [authDataStorage, storageLoadComplete]);

  const authenticateWithToken = useCallback(
    (token: string) => {
      const id = getIdFromToken(token);
      if (id) {
        authenticateSuccess(token, id);
      } else {
        loginFails(LOGIN_MESSAGES.AUTHORIZATION_ERROR);
      }
    },
    [authenticateSuccess, getIdFromToken, loginFails]
  );

  useEffect(() => {
    if (state.tokenLoadedFromStorage && state.token) {
      authenticateWithToken(state.token);
    }
  }, [authenticateWithToken, state.tokenLoadedFromStorage, state.token]);

  useEffect(() => {
    if (authentication.data) {
      const authError = validateAuthentication(authentication.data);

      if (authError) {
        loginFails(LOGIN_MESSAGES[authError]);
      } else {
        authenticateWithToken(authentication.data.accessToken);
      }
    }
  }, [
    authenticateWithToken,
    authentication.data,
    loginFails,
    validateAuthentication,
  ]);

  useEffect(() => {
    async function validate() {
      if (!state.isAuthenticateSuccess) return;
      try {
        const isValid = validateAuthorization
          ? await validateAuthorization?.(state.id)
          : state.id && Promise.resolve(true);
        if (isValid) {
          loginSuccess();
        } else {
          loginFails(LOGIN_MESSAGES.AUTHORIZATION_ERROR);
        }
      } catch (error) {
        console.error(error);
        loginFails(LOGIN_MESSAGES.AUTHORIZATION_ERROR);
      }
    }

    validate();
  }, [
    loginFails,
    loginSuccess,
    state.id,
    state.isAuthenticateSuccess,
    validateAuthorization,
  ]);

  useEffect(() => {
    if (state.isAuthenticateSuccess && state.token) {
      authDataStorage.set(state.token, hostsConfig[environment].oeDomain);
    }
  }, [
    state.isAuthenticateSuccess,
    state.token,
    onLoginSuccess,
    authDataStorage,
    environment,
  ]);

  useEffect(() => {
    if (state.isLoginSuccess && state.token) {
      authDataStorage.set(state.token, hostsConfig[environment].oeDomain);
    }
  }, [
    state.isLoginSuccess,
    state.token,
    onLoginSuccess,
    authDataStorage,
    environment,
  ]);

  useEffect(() => {
    if (state.isLoginSuccess && onLoginSuccess) {
      onLoginSuccess.callback(...onLoginSuccess.params);
    }
  }, [onLoginSuccess, state.isLoginSuccess]);

  const login = (username: string, password: string) =>
    authentication.loginWithUsernameAndPassword({
      env: environment,
      username,
      password,
    });

  const resetUserPasswordWithCallback = async (
    username: string,
    onSuccess: () => void,
    onError: () => void
  ) => {
    try {
      await resetUserPassword(environment, username);
      onSuccess();
    } catch {
      onError();
    }
  };

  const logoutAndRefreshPage = async (redirectTo?: string) => {
    await logoutAsync();
    authDataStorage.remove(hostsConfig[environment].oeDomain).then(() => {
      if (redirectTo) {
        window.location.replace(redirectTo);
      } else {
        window.location.reload();
      }
    });
  };
  return {
    personId: state.id as number,
    contactId: state.id as string,
    loginUuid: state.loginUuid,
    authenticateWithToken,
    logout: logoutAndRefreshPage,
    loginWithUsernameAndPassword: login,
    setOnLoginSuccess,
    resetUserPassword: resetUserPasswordWithCallback,
    loginError: state.error || LOGIN_MESSAGES[authentication.error],
    isLoading: state.isLoading || authentication.isLoading,
    isInitialized:
      state.isLoginSuccess !== undefined &&
      state.tokenLoadedFromStorage === false,
    isLoginSuccess: state.isLoginSuccess,
  };
};

const [LoginProviderCtx, useLoginContext] = constate(LoginContext);
export interface LoginProviderProps extends LoginContextProps {
  queryClient: QueryClient;
  children?: React.ReactNode;
}

const LoginProvider: React.FC<LoginProviderProps> = ({
  children,
  queryClient,
  ...rest
}) => (
  <QueryClientProvider client={queryClient}>
    <LoginProviderCtx {...rest}>{children}</LoginProviderCtx>
  </QueryClientProvider>
);

export { LoginProvider, useLoginContext };
